
















































































































import { computed, defineComponent, ref, PropType, watch } from '@vue/composition-api';
import { ChevronLeftIcon, CloseIcon, ErrorCircleIcon, InfoCircleIcon } from 'tdesign-icons-vue';
import fs from 'fs';
import SelectAnySpaceTargetFolder from '@/renderer/components/dialog/SelectAnySpaceTargetFolder/index.vue';
import SyncTypeCard from '@/renderer/views/desktop/FileManagement/FileSync/components/SelectFolder/components/SyncTypeCard.vue';
import i18n, { i18n_t } from '@/locales/i18n';
import {
  ConflictStrategy,
  CoSyncInfo,
  CoSyncSetting,
  CoSyncStrategy,
  CoSyncTimingType,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import {
  validateIgnoreEntry,
  IgnoreEntryInvalidReason,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/ignoreMatcher';
import { ShowSettingSource } from '@/renderer/views/desktop/FileManagement/FileSync/components/SelectFolder/composition/CoSyncSettingDrawerHandler';
import { getSyncDeleteCachePath } from '@/common/constant/sync';
import { IpcEvents } from '@/ipc-events';
import ipc from '@/renderer/scripts/utils/ipc';
import tips from '@/renderer/scripts/utils/tips';

// 排除目录清单条目数量上限
const IGNORE_LIST_MAX = 50;

export default defineComponent({
  components: { SelectAnySpaceTargetFolder, SyncTypeCard, ChevronLeftIcon, CloseIcon, ErrorCircleIcon, InfoCircleIcon },
  props: {
    showSource: {
      required: true,
      type: Object as PropType<ShowSettingSource>,
    },
    inputSetting: {
      required: true,
      type: Object as PropType<CoSyncSetting>,
    },
  },
  emits: ['update:show', 'onSettingUpdate'],
  setup(props, context) {
    const innerSetting = ref(props.inputSetting);

    const innerShowSource = computed<ShowSettingSource>({
      get: () => props.showSource,
      set: val => {
        context.emit('update:showSource', val as ShowSettingSource);
      },
    });

    const innerShow = computed({
      get: () => innerShowSource.value.source !== 'none',
      set: val => {
        if (!val) innerShowSource.value = { source: 'none' };
        else {
          // do nothing
        }
      },
    });

    const inputeCoSyncInfo = computed<CoSyncInfo | null>(() => {
      if (innerShowSource.value.source === 'setting') return innerShowSource.value.coSyncInfo;
      return null;
    });

    const settingTabs = ref(1);

    // 创建态可编辑、设置态只读：无 coSyncInfo 即创建态
    const isEditable = computed(() => inputeCoSyncInfo.value === null);

    const ignoreListConfig = computed(() => ({
      value: Array.isArray(innerSetting.value.ignoreList) ? innerSetting.value.ignoreList : [],
      onChange: (val: string[]) => {
        const next = Array.isArray(val) ? val : [];
        const current = Array.isArray(innerSetting.value.ignoreList) ? innerSetting.value.ignoreList : [];

        // 删除/清空：长度变短，直接接受（不需要校验）
        if (next.length <= current.length) {
          innerSetting.value.ignoreList = next;
          notifyUpdate();
          return;
        }

        // 新增：仅校验本次新增的尾部条目，非法则拒绝并提示
        const added = next.slice(current.length);
        const accepted: string[] = [...current];
        let invalidReason: IgnoreEntryInvalidReason | null = null;
        let duplicated = false;

        added.forEach(raw => {
          const entry = (raw ?? '').trim().replace(/^\/+/, '').replace(/\/+$/, '');
          const reason = validateIgnoreEntry(raw);
          if (reason) {
            invalidReason = reason;
            return;
          }
          if (accepted.includes(entry)) {
            duplicated = true;
            return;
          }
          accepted.push(entry);
        });

        if (invalidReason) {
          const reasonTextMap: Record<IgnoreEntryInvalidReason, string> = {
            empty: i18n_t('目录名不能为空'),
            tooLong: i18n_t('目录名过长'),
            invalidChar: i18n_t('目录名不支持特殊字符 \\ : * ? " < > |'),
            absolute: i18n_t('请输入相对路径，不支持绝对路径'),
            dotdot: i18n_t('路径不支持包含 ..'),
          };
          tips.error(reasonTextMap[invalidReason]);
        } else if (duplicated) {
          tips.warning(i18n_t('该目录已存在'));
        }

        innerSetting.value.ignoreList = accepted;
        notifyUpdate();
      },
    }));

    const isBiSyncSelected = computed(() => innerSetting.value.strategy === CoSyncStrategy.BOTH);
    const isUniUploadSelected = computed(() => innerSetting.value.strategy === CoSyncStrategy.LOCAL_TO_CLOUD);
    const isUniDownloadSelected = computed(() => innerSetting.value.strategy === CoSyncStrategy.CLOUD_TO_LOCAL);

    const headerWording = computed(() => {
      let syncTypeWording = i18n_t('下载同步');
      if (isBiSyncSelected.value) syncTypeWording = i18n_t('双向同步');
      if (isUniUploadSelected.value) syncTypeWording = i18n_t('上传同步');
      return i18n_t('{syncTypeWording}设置', { syncTypeWording });
    });

    const conflictConfig = computed(() => {
      const ask = { value: ConflictStrategy.ASK, wording: i18n_t('总是询问我'), tips: null };
      const useLocal = {
        value: ConflictStrategy.USE_LOCAL,
        wording: i18n_t('将本地版本上传为云端新版本'),
        tips: i18n_t('未开启历史版本功能，云端版本将被覆盖'),
      };
      const useRemote = { value: ConflictStrategy.USE_REMOTE, wording: i18n_t('使用云端版本覆盖本地版本'), tips: null };
      const keepBoth = { value: ConflictStrategy.KEEP_BOTH, wording: i18n_t('保留全部冲突文件'), tips: null };

      let choices = [ask, useLocal, useRemote, keepBoth];
      if (isUniUploadSelected.value) choices = [ask, useLocal, keepBoth];
      else if (isUniDownloadSelected.value) choices = [ask, useRemote, keepBoth];

      let wording = i18n_t('双向同步同步冲突处理');
      if (isUniUploadSelected.value) wording = i18n_t('上传同步冲突处理');
      else if (isUniDownloadSelected.value) wording = i18n_t('下载同步冲突处理');

      return {
        wording,
        defaultVal: innerSetting.value.conflictStrategy,
        onChange: (checkedValues: ConflictStrategy) => {
          innerSetting.value.conflictStrategy = checkedValues;
        },
        choices,
        tooltipsStyle: (trigger: HTMLElement) => ({ 'word-break': 'break-all' }),
      };
    });

    const timingConfig = computed(() => ({
      wording: i18n_t('同步方式'),
      defaultVal: innerSetting.value.timingType,
      onChange: (checkedValues: CoSyncTimingType) => {
        innerSetting.value.timingType = checkedValues;
      },
      choices: [
        { value: CoSyncTimingType.MANUL, wording: i18n_t('手动同步') },
        { value: CoSyncTimingType.AUTO, wording: i18n_t('自动同步') },
      ],
    }));

    const shouldShowCacheLocation = computed(() => inputeCoSyncInfo.value !== null && isBiSyncSelected.value);
    const cacheLocaltion = computed(() => {
      if (!inputeCoSyncInfo.value) return '';
      return getSyncDeleteCachePath(
        inputeCoSyncInfo.value.config.syncPath.localPath,
        inputeCoSyncInfo.value.localSyncId,
      );
    });
    const isCacheLocaltionExist = computed(() => !fs.existsSync(cacheLocaltion.value));

    const methods = {
      hideSyncSetting() {
        innerShowSource.value = { source: 'none' };
      },

      async openCacheLocaltion() {
        const path = cacheLocaltion.value;
        if (!path) return;
        const isOpen = await ipc.invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, {
          event: 'openPath',
          params: { path },
        });
        if (!isOpen) {
          tips.error(i18n_t('本地文件已被移动、删除或重命名，无法打开'));
        }
      },
    };

    const setup = () => {
      settingTabs.value = 1;
      innerSetting.value = props.inputSetting;
    };

    const notifyUpdate = () => {
      context.emit('onSettingUpdate', innerSetting.value);
    };

    watch(
      () => innerShowSource.value,
      val => {
        if (val !== null) {
          setup();
        } else {
          notifyUpdate();
        }
      },
    );

    return {
      innerShow,

      headerWording,
      settingTabs,
      conflictConfig,

      isEditable,
      ignoreListConfig,
      IGNORE_LIST_MAX,

      shouldShowCacheLocation,
      cacheLocaltion,
      cacheLocaltionTipsStyle: (trigger: HTMLElement) => ({ 'word-break': 'break-all' }),

      timingConfig,

      ...methods,
      isCacheLocaltionExist,
    };
  },
});
